import React, { useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Database, MessageSquare, FileText,
  Activity, ChevronLeft, ChevronRight, Cpu, Zap, Bug,
  ScrollText, LogOut, Building2, Users, Network, GraduationCap, Shield
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { llmApi } from '../api';
import useStore from '../store';
import LoreLogo from './LoreLogo';

function StatusDot({ ok }) {
  return (
    <span style={{
      position: 'relative',
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: ok ? 'var(--green)' : 'var(--red)',
      boxShadow: ok ? '0 0 8px var(--green), 0 0 16px var(--green-glow)' : '0 0 8px var(--red)',
    }}>
      {ok && (
        <span style={{
          position: 'absolute', inset: -3, borderRadius: '50%',
          background: 'var(--green)', opacity: 0.4,
          animation: 'pulseDot 2s ease-in-out infinite',
        }} />
      )}
    </span>
  );
}

export default function Layout({ onLogout }) {
  const { t } = useTranslation('nav');
  const { currentUser, sidebarOpen, toggleSidebar, setHealth } = useStore();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const navItems = [
    { to: '/', icon: LayoutDashboard, label: t('dashboard'), exact: true },
    { to: '/instances', icon: Database, label: t('instances') },
    { to: '/qa', icon: MessageSquare, label: t('qa') },
    { to: '/agent', icon: Network, label: t('agent') },
    { to: '/p/formazione.html', icon: GraduationCap, label: t('aiLearning'), external: true },
    ...(isSuperAdmin ? [{ to: '/p/formazione-admin.html', icon: Shield, label: t('aiLearningAdmin'), external: true }] : []),
    { to: '/prompts', icon: FileText, label: t('prompts') },
    { to: '/debug', icon: Bug, label: t('debug') },
    { to: '/tenants', icon: Building2, label: t('tenants') },
    { to: '/audit', icon: ScrollText, label: t('audit') },
    ...(isSuperAdmin ? [{ to: '/users', icon: Users, label: t('users') }] : []),
  ];
  const location = useLocation();
  const isMobile = () => window.innerWidth <= 640;

  // Close sidebar on mobile by default and on route change
  useEffect(() => {
    if (isMobile() && sidebarOpen) toggleSidebar();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isMobile() && sidebarOpen) toggleSidebar();
  }, [location.pathname]); // eslint-disable-line

  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: llmApi.health,
    refetchInterval: 30000,
    onSuccess: setHealth
  });

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg-0)' }}>
      {/* Mobile overlay backdrop */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar} />}

      {/* Sidebar */}
      <aside
        className={!sidebarOpen ? 'sidebar-mobile-hidden' : 'sidebar-mobile-open'}
        style={{
          width: sidebarOpen ? 248 : 64,
          minWidth: sidebarOpen ? 248 : 64,
          background:
            'radial-gradient(circle at 50% 0%, var(--accent-glow) 0%, transparent 35%),' +
            'linear-gradient(180deg, var(--bg-1) 0%, var(--bg-0) 100%)',
          borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column',
          transition: 'width 0.25s ease, min-width 0.25s ease',
          overflow: 'hidden', position: 'relative', zIndex: 10,
          boxShadow: '4px 0 24px rgba(0,0,0,0.25)',
        }}
      >
        {/* Logo */}
        <div style={{
          padding: '22px 16px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 12,
          position: 'relative',
        }}>
          <div style={{
            flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent2) 100%)',
            boxShadow: '0 0 20px var(--accent-glow-strong)',
          }}>
            <LoreLogo size={22} />
          </div>
          {sidebarOpen && (
            <div>
              <div className="gradient-text" style={{
                fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 17,
                letterSpacing: '0.5px', lineHeight: 1,
              }}>
                LORE
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 500, marginTop: 3 }}>{t('tagline')}</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '14px 10px', overflowY: 'auto' }}>
          {navItems.map(({ to, icon: Icon, label, exact, external }) => external ? (
            <a
              key={to}
              href={to}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                marginBottom: 3, textDecoration: 'none',
                transition: 'all 0.2s ease',
                background: 'transparent',
                color: 'var(--text-2)',
                borderLeft: '2px solid transparent',
                fontWeight: 500,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-3)'; e.currentTarget.style.color = 'var(--text-1)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-2)'; }}
            >
              <Icon size={16} style={{ flexShrink: 0 }} />
              {sidebarOpen && (
                <span style={{ fontSize: 13, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {label}
                  <span style={{ fontSize: 9, color: 'var(--text-3)' }}>↗</span>
                </span>
              )}
            </a>
          ) : (
            <NavLink
              key={to}
              to={to}
              end={exact}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                marginBottom: 3, textDecoration: 'none',
                transition: 'all 0.2s ease',
                background: isActive
                  ? 'linear-gradient(90deg, var(--accent-glow) 0%, transparent 100%)'
                  : 'transparent',
                color: isActive ? 'var(--text-1)' : 'var(--text-2)',
                borderLeft: isActive
                  ? '2px solid var(--accent)'
                  : '2px solid transparent',
                fontWeight: isActive ? 600 : 500,
                position: 'relative',
                boxShadow: isActive ? 'inset 0 0 20px var(--accent-glow)' : 'none',
              })}
              onMouseEnter={e => {
                if (!e.currentTarget.style.background.includes('gradient')) {
                  e.currentTarget.style.background = 'var(--bg-3)';
                  e.currentTarget.style.color = 'var(--text-1)';
                }
              }}
              onMouseLeave={e => {
                if (!e.currentTarget.style.boxShadow.includes('inset')) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--text-2)';
                }
              }}
            >
              <Icon size={16} style={{ flexShrink: 0 }} />
              {sidebarOpen && <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Status panel */}
        {sidebarOpen && health && (
          <div className="glass" style={{
            margin: '0 10px 8px', padding: 14, borderRadius: 'var(--radius-sm)',
          }}>
            <div style={{ fontSize: 9, color: 'var(--text-3)', marginBottom: 10, letterSpacing: '1.5px', fontWeight: 700 }}>
              ⚡ {t('systemStatus')}
            </div>
            {Object.entries(health.services || {}).map(([key, svc]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <StatusDot ok={svc.status === 'online' || svc.status === 'configured'} />
                <span style={{ fontSize: 10, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{key}</span>
              </div>
            ))}
          </div>
        )}

        {/* Logout button */}
        {onLogout && (
          <button
            onClick={onLogout}
            title="Logout"
            style={{
              margin: '0 8px 4px', padding: '8px 12px',
              borderRadius: 'var(--radius-sm)',
              background: 'transparent', border: '1px solid var(--border)',
              color: 'var(--text-3)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 12,
              transition: 'all 0.15s', fontSize: 13,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)'; }}
          >
            <LogOut size={14} style={{ flexShrink: 0 }} />
            {sidebarOpen && <span style={{ whiteSpace: 'nowrap' }}>{t('logout')}</span>}
          </button>
        )}

        {/* Toggle btn */}
        <button
          onClick={toggleSidebar}
          style={{
            margin: '0 8px 12px', padding: 8, borderRadius: 'var(--radius-sm)',
            background: 'var(--bg-3)', border: '1px solid var(--border)',
            color: 'var(--text-3)', cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s'
          }}
        >
          {sidebarOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, overflow: 'auto', background: 'transparent', position: 'relative' }}>
        <div style={{ minHeight: '100%', padding: 0 }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
